import { Text } from '@/components/DesignSystem';
import { PointsAuditEndpoint } from '@api/endpoints';
import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { RewardsProgramType } from '@shared/enums';
import { useTranslation } from 'react-i18next';
import {
  describeMovementReason,
  formatSignedPoints,
  reasonsForFarmer,
  tiersForFarmer,
} from './movementReasons';
import { programTypeLabel } from './programTypeLabel';
import { scrollingTableSx } from './auditLayout';
import { walletDetail } from './walletDetail';

type ProcessImpact = PointsAuditEndpoint.GetProcess.ProcessImpact;

const NO_WRAP = { whiteSpace: 'nowrap' } as const;
// Beside several wallet rows, a centred farmer reads as belonging to the middle one.
const SPANNED_CELL = { verticalAlign: 'top' } as const;
type ProcessImpactWallet = PointsAuditEndpoint.GetProcess.ProcessImpactWallet;

/** A farmer with no wallet breakdown still gets one line, carrying their totals. */
const walletsOf = (row: ProcessImpact): ProcessImpactWallet[] => (
  row.wallets.length
    ? row.wallets
    : [{
      bookingExternalId: null,
      expirationDate: null,
      isBookingReserve: false,
      name: null,
      pointsAfter: row.pointsAfter,
      pointsBefore: row.pointsBefore,
      pointsDelta: row.pointsDelta,
      walletKey: 'total',
    }]
);

interface ProcessImpactTableProps {
  impact: ProcessImpact[],
  maxHeight?: number,
  onSelectFarmer?: (farmer: ProcessImpact) => void,
  /** Omitted for revert impact, which has no promotion or redemption behind it. */
  reasons?: PointsAuditEndpoint.GetProcess.ProcessReason[],
}

/**
 * Who a process moved points for, and each touched wallet's balance either side. A null balance is
 * an em dash: not known, which a 0 would misstate as "nothing moved". A farmer moved in both
 * programs has rows for each, since energy and agronomy points never net.
 */
export const ProcessImpactTable = ({
  impact,
  maxHeight = 220,
  onSelectFarmer,
  reasons,
}: ProcessImpactTableProps) => {
  const { t } = useTranslation();

  if (!impact.length) {
    return <Text category="body-small">{t($ => $.pointsAudit.revert.noImpact)}</Text>;
  }

  const format = (points: number | null) => (points === null ? '—' : points.toLocaleString());
  const showReasons = !!reasons?.length;
  const showProgram = impact.some(({ programType }) => programType === RewardsProgramType.Energy);
  // Agronomy rows keep the ids they had before energy rows existed.
  const rowKey = (row: ProcessImpact) => (
    row.programType === RewardsProgramType.Energy ? `${row.farmerId}-energy` : row.farmerId
  );

  return (
    <Stack gap={0.5}>
      <TableContainer component={Paper} sx={scrollingTableSx(maxHeight)}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t($ => $.pointsAudit.revert.farmer)}</TableCell>
              {showProgram && <TableCell>{t($ => $.pointsAudit.processes.program)}</TableCell>}
              {showReasons && <TableCell>{t($ => $.pointsAudit.reason.why)}</TableCell>}
              {showReasons && <TableCell>{t($ => $.pointsAudit.ledger.tier)}</TableCell>}
              <TableCell>{t($ => $.pointsAudit.revert.wallet)}</TableCell>
              <TableCell align="right" sx={NO_WRAP}>
                {t($ => $.pointsAudit.revert.walletBefore)}
              </TableCell>
              <TableCell align="right" sx={NO_WRAP}>
                {t($ => $.pointsAudit.revert.walletAfter)}
              </TableCell>
              <TableCell align="right" sx={NO_WRAP}>
                {t($ => $.pointsAudit.revert.pointsChange)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {impact.flatMap((row) => walletsOf(row).map((wallet, walletIndex) => {
              const isFirstWallet = walletIndex === 0;
              const rowSpan = walletsOf(row).length;
              return (
                <TableRow
                  data-testid={isFirstWallet
                    ? `revert-impact-${rowKey(row)}`
                    : `revert-impact-${rowKey(row)}-${wallet.walletKey}`}
                  hover={!!onSelectFarmer}
                  key={`${rowKey(row)}-${wallet.walletKey}`}
                  onClick={onSelectFarmer ? () => onSelectFarmer(row) : undefined}
                  sx={onSelectFarmer ? { cursor: 'pointer' } : undefined}
                >
                  {isFirstWallet && (
                    <TableCell rowSpan={rowSpan} sx={SPANNED_CELL}>
                      {row.name || row.businessName || row.externalId}
                    </TableCell>
                  )}
                  {isFirstWallet && showProgram && (
                    <TableCell rowSpan={rowSpan} sx={SPANNED_CELL}>
                      {programTypeLabel(row.programType, t)}
                    </TableCell>
                  )}
                  {isFirstWallet && showReasons && (
                    <TableCell data-testid={`impact-reasons-${rowKey(row)}`} rowSpan={rowSpan} sx={SPANNED_CELL}>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {reasonsForFarmer(reasons, row.farmerId, row.programType).map((reason) => (
                          <Chip
                            key={reason.reason}
                            label={t($ => $.pointsAudit.reason.chip, {
                              points: formatSignedPoints(reason.pointsDelta),
                              reason: describeMovementReason(reason.reason, t),
                            })}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  )}
                  {isFirstWallet && showReasons && (
                    <TableCell data-testid={`impact-tiers-${rowKey(row)}`} rowSpan={rowSpan} sx={SPANNED_CELL}>
                      {tiersForFarmer(reasons, row.farmerId, row.programType).length === 0
                        ? '\u2014'
                        : (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {tiersForFarmer(reasons, row.farmerId, row.programType).map((tier) => (
                              <Chip
                                key={tier.tierRank}
                                label={tier.tierName ?? `#${tier.tierRank}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Text category="body-medium">{wallet.name ?? '\u2014'}</Text>
                    {!!walletDetail(wallet, t) && (
                      <Text category="body-small" sx={{ color: 'text.secondary' }}>
                        {walletDetail(wallet, t)}
                      </Text>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={NO_WRAP}>{format(wallet.pointsBefore)}</TableCell>
                  <TableCell align="right" sx={NO_WRAP}>{format(wallet.pointsAfter)}</TableCell>
                  <TableCell align="right" sx={NO_WRAP}>
                    {formatSignedPoints(wallet.pointsDelta)}
                  </TableCell>
                </TableRow>
              );
            }))}
          </TableBody>
        </Table>
      </TableContainer>
      <Text category="body-small">{t($ => $.pointsAudit.revert.walletBalanceNote)}</Text>
    </Stack>
  );
};
